import React, { useEffect, useState } from 'react';
import useDragTranslate from '../hooks/useDragTranslate';
import { getRandomWindowPosition } from '../utils/windowPositioning';
/* education window component */

const EducationWindow = ({ onClose, zIndex, onFocus }) => {
  const { elRef, onPointerDown, onPointerMove, onPointerUp } = useDragTranslate(onFocus);
  /* drag functionality for this window */
  
  // Generate random position for this window
  const [windowPosition] = useState(() => getRandomWindowPosition(900, 700));

  // Function to calculate color based on difficulty (1=green, 5=yellow, 10=red)
  const getDifficultyColor = (difficulty) => {
    // Clamp difficulty between 1 and 10
    const clamped = Math.max(1, Math.min(10, difficulty));
    
    if (clamped <= 5) {
      // 1-5: Green to Yellow
      const ratio = (clamped - 1) / 4; // 0 to 1
      const r = Math.round(255 * ratio);
      const g = 255;
      const b = 0;
      return `rgb(${r}, ${g}, ${b})`;
    } else {
      // 6-10: Yellow to Red
      const ratio = (clamped - 5) / 5; // 0 to 1
      const r = 255;
      const g = Math.round(255 * (1 - ratio));
      const b = 0;
      return `rgb(${r}, ${g}, ${b})`;
    }
  };
  
  const handleFocus = () => {
    onFocus && onFocus();
    /* bring window to front when clicked */
  };

  const handleClose = () => {
    // 1) add .closing
    elRef.current?.classList.add('closing');
    /* start the close animation */

    // 2) after animation end, call onClose to unmount
    const done = () => {
      elRef.current?.removeEventListener('animationend', done);
      onClose();
    };
    elRef.current?.addEventListener('animationend', done, { once: true });
    /* wait for animation to finish then actually close */
  };

  useEffect(() => {
    // listen on the document so we can move outside the window bounds
    document.addEventListener('pointermove', onPointerMove);
    document.addEventListener('pointerup', onPointerUp);
    /* set up drag event listeners */
    return () => {
      document.removeEventListener('pointermove', onPointerMove);
      document.removeEventListener('pointerup', onPointerUp);
      /* clean up listeners */
    };
  }, [onPointerMove, onPointerUp]);

  return (
    <div
      ref={elRef}
      className="window education-window"
      data-window="education"
      style={{
        display: 'block',
        width: '900px',
        height: '700px',
        zIndex: zIndex,
        position: 'fixed',
        '--tx': `${windowPosition.tx}px`,
        '--ty': `${windowPosition.ty}px`,
        // important: do not set left/top/transform here; css handles it.
      }}
      onClick={handleFocus}
    >
      <div className="title-bar draggable" onPointerDown={onPointerDown}>
        <div className="title-bar-text">Education - Windows Explorer</div>
        <div className="title-bar-controls">
          <button aria-label="Close" onClick={handleClose}></button>
        </div>
      </div>
      {/* window title bar with close button */}
      
      <div className="window-body project-detail-body">
        <div className="project-content">
          <h2 className="section-title">Education</h2>
          <div className="project-section">
            <div className="section-content">
              <div className="trivia-questions">
                <div className="trivia-item">
                  <span className="trivia-question">School:</span>
                  <span className="trivia-answer">University of California, Riverside</span>
                </div>
                <div className="trivia-item">
                  <span className="trivia-question">Degree:</span>
                  <span className="trivia-answer">B.S. in CS w/ Business Applications</span>
                </div>
                <div className="trivia-item">
                  <span className="trivia-question">Span:</span>
                  <span className="trivia-answer">09/2022 - 12/2026</span>
                </div>
                <div className="trivia-item">
                  <span className="trivia-question">GPA:</span>
                  <span className="trivia-answer">3.82 / 4.00 (Chancellors List)</span>
                </div>
              </div>
            </div>
          </div>

          <div className="project-section">
            <h2 className="section-title">Involvement</h2>
            <div className="section-content">
              <div className="accolades-row">
                <span>• Secretary, Quantitative Analysis Club</span>
                <span>• Member, Armenian Student Association (ASA)</span>
                <span>• Member, Association for Computing Machinery (ACM)</span>
              </div>
              <div className="accolades-row">
                <span>• Attendee, CutieHack x3</span>
                <span>• Attendee, CitrusHack x2</span>
                <span>• Judge, Citrus Hack</span>
              </div>
            </div>
          </div>

          <div className="project-section">
            <div className="coursework-grid">
              <div className="coursework-column">
                <h2 className="section-title">CS Coursework <span style={{color: 'red'}}>(difficulty/10)</span></h2>
                <div className="section-content">
                  <p className="project-paragraph">• Object Oriented Programming (C++) <span style={{float: 'right', color: getDifficultyColor(5), backgroundColor: '#666666', padding: '2px 6px', marginRight: '20px', borderRadius: '3px'}}>5</span></p>
                  <p className="project-paragraph">• Data Structures and Algorithms <span style={{float: 'right', color: getDifficultyColor(6), backgroundColor: '#666666', padding: '2px 6px', marginRight: '20px', borderRadius: '3px'}}>6</span></p>
                  <p className="project-paragraph">• Software Construction <span style={{float: 'right', color: getDifficultyColor(3), backgroundColor: '#666666', padding: '2px 6px', marginRight: '20px', borderRadius: '3px'}}>3</span></p>
                  <p className="project-paragraph">• Software Engineering <span style={{float: 'right', color: getDifficultyColor(4), backgroundColor: '#666666', padding: '2px 6px', marginRight: '20px', borderRadius: '3px'}}>4</span></p>
                  <p className="project-paragraph">• Unit Testing <span style={{float: 'right', color: getDifficultyColor(5), backgroundColor: '#666666', padding: '2px 6px', marginRight: '20px', borderRadius: '3px'}}>5</span></p>
                  <p className="project-paragraph">• Database Management Systems <span style={{float: 'right', color: getDifficultyColor(5), backgroundColor: '#666666', padding: '2px 6px', marginRight: '20px', borderRadius: '3px'}}>5</span></p>
                  <p className="project-paragraph">• Operating Systems <span style={{float: 'right', color: getDifficultyColor(8), backgroundColor: '#666666', padding: '2px 6px', marginRight: '20px', borderRadius: '3px'}}>8</span></p>
                  <p className="project-paragraph">• Logic Design <span style={{float: 'right', color: getDifficultyColor(4), backgroundColor: '#666666', padding: '2px 6px', marginRight: '20px', borderRadius: '3px'}}>4</span></p>
                  <p className="project-paragraph">• Machine Organization & Assembly <span style={{float: 'right', color: getDifficultyColor(7), backgroundColor: '#666666', padding: '2px 6px', marginRight: '20px', borderRadius: '3px'}}>7</span></p>
                  <p className="project-paragraph">• Discrete Structures <span style={{float: 'right', color: getDifficultyColor(10), backgroundColor: '#666666', padding: '2px 6px', marginRight: '20px', borderRadius: '3px'}}>10</span></p>
                </div>
              </div>
              <div className="coursework-column">
                <h2 className="section-title">Business Coursework <span style={{color: 'red'}}>(difficulty/10)</span></h2>
                <div className="section-content">
                  <p className="project-paragraph">• Financial Accounting and Reporting <span style={{float: 'right', color: getDifficultyColor(1), backgroundColor: '#666666', padding: '2px 6px', marginRight: '20px', borderRadius: '3px'}}>1</span></p>
                  <p className="project-paragraph">• Decision Analysis and Management Science <span style={{float: 'right', color: getDifficultyColor(1), backgroundColor: '#666666', padding: '2px 6px', marginRight: '20px', borderRadius: '3px'}}>1</span></p>
                  <p className="project-paragraph">• Marketing & Business Management <span style={{float: 'right', color: getDifficultyColor(1), backgroundColor: '#666666', padding: '2px 6px', marginRight: '20px', borderRadius: '3px'}}>1</span></p>
                  <p className="project-paragraph">• Microeconomics <span style={{float: 'right', color: getDifficultyColor(1), backgroundColor: '#666666', padding: '2px 6px', marginRight: '20px', borderRadius: '3px'}}>1</span></p>
                  <p className="project-paragraph">• Macroeconomics <span style={{float: 'right', color: getDifficultyColor(1), backgroundColor: '#666666', padding: '2px 6px', marginRight: '20px', borderRadius: '3px'}}>1</span></p>
                  <p className="project-paragraph">• Business Analytics <span style={{float: 'right', color: getDifficultyColor(1), backgroundColor: '#666666', padding: '2px 6px', marginRight: '20px', borderRadius: '3px'}}>1</span></p>
                  <p className="project-paragraph">• Business Simulation <span style={{float: 'right', color: getDifficultyColor(1), backgroundColor: '#666666', padding: '2px 6px', marginRight: '20px', borderRadius: '3px'}}>1</span></p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EducationWindow;
